import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Eye, Edit, Pause, Play, MessageSquare, X, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface AdoptionPost {
  id: string;
  title: string;
  pet_name: string;
  species: string;
  breed?: string;
  age?: number;
  size?: string;
  gender?: string;
  status: 'draft' | 'published' | 'paused' | 'closed' | 'adopted';
  requests_count: number;
  last_activity_at: string;
  image_url?: string;
  location: string;
  description?: string;
}

interface AdoptionPostCardProps {
  post: AdoptionPost;
  onEdit: (post: AdoptionPost) => void;
  onTogglePause: (id: string, currentStatus: string) => void;
  onViewRequests: (id: string) => void;
  onClose: (id: string) => void;
  onDelete: (id: string) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'draft': return 'bg-muted text-muted-foreground';
    case 'published': return 'bg-green-500/20 text-green-700 border-green-500/20';
    case 'paused': return 'bg-orange-500/20 text-orange-700 border-orange-500/20';
    case 'closed': return 'bg-red-500/20 text-red-700 border-red-500/20';
    case 'adopted': return 'bg-blue-500/20 text-blue-700 border-blue-500/20';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'draft': return 'Bozza';
    case 'published': return 'Pubblicato';
    case 'paused': return 'In pausa';
    case 'closed': return 'Chiuso';
    case 'adopted': return 'Adottato';
    default: return status;
  }
};

export default function AdoptionPostCard({ 
  post, 
  onEdit, 
  onTogglePause, 
  onViewRequests, 
  onClose, 
  onDelete 
}: AdoptionPostCardProps) {
  const [showActions, setShowActions] = useState(false);

  return (
    <Card className="glass border-white/20 hover:border-white/30 transition-colors">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={post.image_url} alt={post.pet_name} />
              <AvatarFallback>{post.pet_name[0]}</AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <h3 className="font-semibold text-white">{post.title}</h3>
              <p className="text-sm text-muted-foreground">
                {post.pet_name} • {post.species} {post.breed && `• ${post.breed}`}
              </p>
              <p className="text-xs text-muted-foreground">{post.location}</p>
            </div>
          </div>
          <Badge className={getStatusColor(post.status)}>
            {getStatusLabel(post.status)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{post.requests_count} richieste</span>
          <span>
            Ultimo aggiornamento: {format(new Date(post.last_activity_at), 'dd MMM', { locale: it })}
          </span>
        </div>

        {/* Action Buttons - Optimized for all zoom levels */}
        <div className="flex flex-wrap justify-center gap-2 mt-4 p-3 bg-black/20 rounded-lg border border-white/10">
          <Button
            variant="outline"
            size="default"
            onClick={() => onEdit(post)}
            className="flex-1 min-w-[100px] max-w-[130px] h-10 border-primary/30 hover:bg-primary/10 text-sm font-medium px-3"
          >
            <Edit className="w-4 h-4 mr-1.5 flex-shrink-0" />
            <span className="truncate">Modifica</span>
          </Button>

          {(post.status === 'published' || post.status === 'paused') && (
            <Button
              variant="outline"
              size="default"
              onClick={() => onTogglePause(post.id, post.status)}
              className="flex-1 min-w-[100px] max-w-[130px] h-10 border-primary/30 hover:bg-primary/10 text-sm font-medium px-3"
            >
              {post.status === 'published' ? (
                <Pause className="w-4 h-4 mr-1.5 flex-shrink-0" />
              ) : (
                <Play className="w-4 h-4 mr-1.5 flex-shrink-0" />
              )}
              <span className="truncate">
                {post.status === 'published' ? 'Pausa' : 'Riprendi'}
              </span>
            </Button>
          )}

          {post.requests_count > 0 && (
            <Button
              variant="outline"
              size="default"
              onClick={() => onViewRequests(post.id)}
              className="flex-1 min-w-[100px] max-w-[140px] h-10 border-green-500/30 text-green-400 hover:bg-green-500/10 text-sm font-medium px-3"
            >
              <MessageSquare className="w-4 h-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">Richieste ({post.requests_count})</span>
            </Button>
          )}

          {post.status !== 'adopted' && post.status !== 'closed' && (
            <Button
              variant="outline"
              size="default"
              onClick={() => onClose(post.id)}
              className="flex-1 min-w-[100px] max-w-[130px] h-10 border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm font-medium px-3"
            >
              <X className="w-4 h-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">Chiudi</span>
            </Button>
          )}

          {post.status === 'draft' && (
            <Button
              variant="outline"
              size="default"
              onClick={() => onDelete(post.id)}
              className="flex-1 min-w-[100px] max-w-[130px] h-10 border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm font-medium px-3"
            >
              <Trash2 className="w-4 h-4 mr-1.5 flex-shrink-0" />
              <span className="truncate">Elimina</span>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}